import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Settings as SettingsIcon } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import Actions from './Actions';

interface IConfigItem {
  key: string;
  value: string | boolean | number;
  type: string;
  isPublic: boolean;
  showInFeatureToggle: boolean;
  description: string;
}

const Toggle = ({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) => (
  <button
    onClick={onChange}
    disabled={disabled}
    className={`relative w-12 h-7 rounded-full transition-colors ${disabled ? 'bg-gray-200 opacity-50 cursor-not-allowed' : checked ? 'bg-[#030213]' : 'bg-gray-300'}`}
    role="switch"
    aria-checked={checked}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${checked && !disabled ? 'translate-x-5' : ''}`}
    />
  </button>
);

const FeatureTogglesSection = () => {
  const { t } = useTranslation();

  const [config, setConfig] = useState<IConfigItem[]>([]);
  const [dirty, setDirty] = useState<Record<string, string | boolean | number>>(
    {},
  );
  const [saving, setSaving] = useState(false);

  const configLabel = (key: string, dbDescription: string) => {
    const titleKey = `platform_config.${key}`;
    const descKey = `platform_config.${key}_desc`;
    const title = t(titleKey);
    const desc = t(descKey);

    return {
      title: title !== titleKey ? title : dbDescription || key,
      desc: desc !== descKey ? desc : dbDescription || key,
    };
  };

  useEffect(() => {
    const load = async () => {
      try {
        const items = await api.get<IConfigItem[]>(
          '/platform-config/backoffice',
        );
        setConfig(items);
      } catch (err) {
        console.error('Failed to load platform config:', err);
      }
    };
    load();
  }, []);

  const handleToggle = (key: string) => {
    const item = config.find((c) => c.key === key);
    const current = dirty[key] ?? item?.value ?? false;
    const newValue = !current;
    const updates: Record<string, string | boolean | number> = {
      [key]: newValue,
    };

    if (key === 'registration.requireMobileNumber' && !newValue) {
      updates['registration.requireOtp'] = false;
    }

    setDirty((prev) => ({ ...prev, ...updates }));
  };

  const handleValueChange = (key: string, value: string | number) => {
    setDirty((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const entries = Object.entries(dirty).map(([key, value]) => ({
        key,
        value: String(value),
      }));

      if (entries.length > 0) {
        const updated = await api.patch<IConfigItem[]>(
          '/platform-config',
          entries,
        );
        setConfig(updated);
      }

      setDirty({});
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const hasDirty = Object.keys(dirty).length > 0;
  const booleanItems = config.filter(
    (c) => c.type === 'boolean' && c.showInFeatureToggle,
  );
  const inputItems = config.filter(
    (c) => (c.type === 'number' || c.type === 'text') && c.showInFeatureToggle,
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-5">
          <SettingsIcon className="w-5 h-5 text-foreground" />
          <h3 className="text-base font-medium text-foreground">
            {t('config.feature_control')}
          </h3>
        </div>

        {/* Boolean toggles */}
        <div className="divide-y divide-gray-100">
          {booleanItems.map((item) => {
            const currentValue =
              (dirty[item.key] as boolean) ?? (item.value as boolean);
            const meta = configLabel(item.key, item.description);

            const mobileItem = config.find(
              (c) => c.key === 'registration.requireMobileNumber',
            );
            const mobileEnabled =
              (dirty['registration.requireMobileNumber'] as boolean) ??
              (mobileItem?.value as boolean) ??
              false;
            const isOtpDisabled =
              item.key === 'registration.requireOtp' && !mobileEnabled;

            return (
              <div
                key={item.key}
                className="flex items-center justify-between py-4"
              >
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {meta.title}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    {meta.desc}
                    {isOtpDisabled && (
                      <span className="ml-2 text-xs text-orange-500">
                        ({t('config.requires_mobile_number')})
                      </span>
                    )}
                  </div>
                </div>
                <Toggle
                  checked={isOtpDisabled ? false : currentValue}
                  onChange={() => handleToggle(item.key)}
                  disabled={isOtpDisabled}
                />
              </div>
            );
          })}
        </div>

        {/* Number and text inputs */}
        {inputItems.length > 0 && (
          <div className="mt-4 space-y-5 border-t border-border pt-4">
            {inputItems.map((item) => {
              const currentValue = dirty[item.key] ?? item.value;
              const meta = configLabel(item.key, item.description);

              return (
                <div key={item.key}>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {meta.title}
                  </label>
                  <Input
                    type={item.type === 'text' ? 'text' : 'number'}
                    value={currentValue as string | number}
                    onChange={(e) =>
                      handleValueChange(
                        item.key,
                        item.type === 'number'
                          ? Number(e.target.value)
                          : e.target.value,
                      )
                    }
                    placeholder={t('config.input_placeholder', {
                      field: meta.title,
                    })}
                  />
                </div>
              );
            })}
          </div>
        )}

        <Actions
          onCancel={() => setDirty({})}
          onSave={handleSave}
          saving={saving}
          disabled={!hasDirty}
        />
      </CardContent>
    </Card>
  );
};

export default FeatureTogglesSection;
